<template>
  <div class="lp">

    <!-- ===== HERO IMAGE ===== -->
    <section class="lp-hero-img">
      <img src="@/assets/1.jpg" alt="김포한강푸르지오리버프론트" />
      <img src="@/assets/2.jpg" alt="김포한강푸르지오리버프론트" />
    </section>

    <!-- ===== 사업 개요 ===== -->
    <section class="lp-section lp-overview-head">
      <div class="lp-inner">
        <span class="lp-eyebrow">— O V E R V I E W —</span>
        <h2 class="lp-section-title">
          김포한강<br/>푸르지오 리버프론트<br/>
          <em>사업 개요</em>
        </h2>
        <div class="lp-gold-line"></div>
      </div>
    </section>

    <!-- ===== OVERVIEW ROLLING (조감도) ===== -->
    <section class="lp-ovslider">
      <div class="ovs-viewport">
        <div
          class="ovs-slide"
          v-for="(src, i) in overviewSlides"
          :key="i"
          :class="{ active: activeSlide === i }">
          <img :src="src" :alt="`김포한강푸르지오리버프론트 조감도 ${i + 1}`" />
        </div>
      </div>
      <div class="ovs-dots">
        <button
          v-for="(src, i) in overviewSlides"
          :key="i"
          class="ovs-dot"
          :class="{ active: activeSlide === i }"
          @click="goSlide(i)"
          :aria-label="`${i + 1}번째 조감도 보기`"></button>
      </div>
    </section>

    <!-- ===== 사업 개요 - 데이터 ===== -->
    <section class="lp-section lp-overview">
      <div class="lp-inner">
        <dl class="ov-list">
          <div class="ov-row" v-for="(it, i) in overviewItems" :key="i">
            <dt class="ov-label">{{ it.label }}</dt>
            <dd class="ov-value" v-html="it.value"></dd>
          </div>
        </dl>
        <p class="ov-note">※ 금회 공급분은 공동주택에 한합니다.</p>
      </div>
    </section>

    <!-- ===== LOCATION (입지환경) ===== -->
    <section class="lp-section lp-location">
      <div class="lp-inner">
        <span class="lp-eyebrow">— L O C A T I O N —</span>
        <h2 class="lp-section-title">
          서울을 더 빠르게,<br/>
          <em>한강 조망은 눈앞에</em>
        </h2>
        <div class="lp-gold-line"></div>
        <p class="loc-lead">김포가 기다린 한강, <strong>푸르지오가 완성할 프리미엄</strong></p>

        <ul class="loc-list">
          <li class="loc-item" v-for="(it, i) in locationItems" :key="i">
            <div class="loc-head">
              <span class="loc-num">{{ it.no }}</span>
              <span class="loc-en">{{ it.en }}</span>
            </div>
            <h3 class="loc-title">{{ it.title }}</h3>
            <p class="loc-desc" v-html="it.desc"></p>
          </li>
        </ul>

        <figure class="loc-map">
          <img src="@/assets/4.jpg" alt="김포한강푸르지오리버프론트 입지도" loading="lazy" />
        </figure>
      </div>
    </section>

    <!-- ===== THE PREMIUM 6 ===== -->
    <section class="lp-section lp-premium">
      <div class="lp-inner">
        <span class="lp-eyebrow">— T H E &nbsp; P R E M I U M —</span>
        <h2 class="lp-section-title">
          김포한강 푸르지오 리버프론트를<br/>완성하는
          <em>여섯 가지 프리미엄</em>
        </h2>
        <div class="lp-gold-line"></div>

        <div class="prem-grid">
          <article
            class="prem-card"
            :class="{ 'is-in': premiumIn.includes(i) }"
            :style="{ transitionDelay: (i % 2) * 90 + 'ms' }"
            v-for="(it, i) in premiumItems"
            :key="i"
            ref="premCards">
            <figure class="prem-fig">
              <img :src="it.img" :alt="`김포한강푸르지오리버프론트 프리미엄 ${i + 1} · ${it.title}`" loading="lazy" />
              <span class="prem-chip">{{ it.tag }}</span>
            </figure>
            <div class="prem-body">
              <h3 class="prem-title">{{ it.title }}</h3>
              <p class="prem-desc" v-html="it.desc"></p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ===== PLOT PLAN ===== -->
    <section class="lp-section lp-plot">
      <div class="lp-inner center">
        <span class="lp-eyebrow">— P L O T &nbsp; P L A N —</span>
        <h2 class="lp-section-title">
          총 2,432세대, 12개동의<br/>
          <em>단지 배치</em>
        </h2>
        <div class="lp-gold-line"></div>

        <p class="plot-lead">
          지하 4층 ~ 지상 38층, <strong>김포한강푸르지오리버프론트</strong>의 단지 구성을 한눈에.
        </p>

        <div class="plot-item">
          <h3 class="plot-subtitle">단지 배치도</h3>
          <figure class="plot-fig">
            <img class="plot-img" src="@/assets/단지배치도.jpg" alt="김포한강푸르지오리버프론트 단지배치도" />
          </figure>
        </div>

        <div class="plot-item">
          <h3 class="plot-subtitle">동호 배치도</h3>
          <figure class="plot-fig">
            <img class="plot-img" src="@/assets/동호배치도.jpg" alt="김포한강푸르지오리버프론트 동호배치도" />
          </figure>
        </div>

        <p class="plot-cap">※ 본 배치도는 이해를 돕기 위한 자료이며 실제와 다를 수 있습니다.</p>
      </div>
    </section>

    <!-- ===== COMMUNITY ===== -->
    <section class="lp-section lp-community">
      <div class="lp-inner">
        <span class="lp-eyebrow">— C O M M U N I T Y —</span>
        <h2 class="lp-section-title">
          크고 작은 여유와 즐거움이 깃든<br/>
          <em>특별한 커뮤니티 라이프</em>
        </h2>
        <div class="lp-gold-line"></div>
        <p class="comm-lead">건강과 균형이 공존하는 <strong>웰니스 공간</strong>이 일상을 새롭게 채웁니다.</p>

        <div class="comm-zone" v-for="(zone, zi) in communityZones" :key="zi">
          <div class="comm-zone-head">
            <span class="comm-zone-floor">{{ zone.floor }}</span>
            <h3 class="comm-zone-name">{{ zone.name }}</h3>
            <p class="comm-zone-tagline">{{ zone.tagline }}</p>
          </div>

          <figure class="comm-map">
            <img :src="zone.img" :alt="`김포한강푸르지오리버프론트 커뮤니티 ${zone.name} 배치도`" loading="lazy" />
          </figure>

          <ul class="comm-list">
            <li class="comm-item" v-for="(f, fi) in zone.facilities" :key="fi">
              <h4 class="comm-item-name">{{ f.name }}</h4>
              <p class="comm-item-desc">{{ f.desc }}</p>
            </li>
          </ul>
        </div>

        <div class="comm-notes">
          <p v-for="(note, ni) in communityNotes" :key="ni">※ {{ note }}</p>
        </div>
      </div>
    </section>

    <!-- ===== UNIT ===== -->
    <section class="lp-section lp-unit">
      <div class="lp-inner center">
        <span class="lp-eyebrow">— U N I T &nbsp; P L A N —</span>
        <h2 class="lp-section-title">
          전용 84 ~ 180㎡,<br/>
          <em>다양한 평면 라인업</em>
        </h2>
        <div class="lp-gold-line"></div>

        <div class="unit-tabs">
          <button
            v-for="(u, i) in unitTypes"
            :key="u.label"
            class="unit-tab"
            :class="{ active: activeUnit === i }"
            @click="setUnit(i)">
            <span class="ut-label">{{ u.label }}</span>
          </button>
        </div>

        <div class="unit-panel">
          <div class="unit-img-wrap">
            <img
              :src="getUnitImg(currentUnit.image)"
              :alt="currentUnit.label + ' 평면도'"
              class="unit-img"
              :class="{ 'is-loading': unitLoading }"
              @load="onUnitImgLoad"
            />
            <transition name="unit-fade">
              <div v-if="unitLoading" class="unit-loading">
                <span class="unit-spinner"></span>
                <span class="unit-loading-label">LOADING</span>
              </div>
            </transition>
          </div>

          <div class="unit-info">
            <div class="unit-info-head">
              <span class="unit-info-type">{{ currentUnit.label }}</span>
              <span class="unit-info-count">총 {{ currentUnit.count }}세대</span>
            </div>
            <dl class="unit-spec">
              <div class="unit-spec-row"><dt>전용면적</dt><dd>{{ currentUnit.ex }}㎡</dd></div>
              <div class="unit-spec-row"><dt>공급면적</dt><dd>{{ currentUnit.sup }}㎡</dd></div>
              <div class="unit-spec-row"><dt>주거공용면적</dt><dd>{{ currentUnit.resi }}㎡</dd></div>
              <div class="unit-spec-row"><dt>기타공용면적</dt><dd>{{ currentUnit.etc }}㎡</dd></div>
              <div class="unit-spec-row unit-spec-total"><dt>계약면적</dt><dd>{{ currentUnit.con }}㎡</dd></div>
            </dl>
          </div>
        </div>

        <p class="unit-note">※ 상기 면적 및 세대수는 인허가 과정에서 일부 변경될 수 있습니다.</p>
      </div>
    </section>

    <!-- ===== LANDSCAPE (단지 조경) ===== -->
    <section class="lp-section lp-landscape">
      <div class="lp-inner center">
        <span class="lp-eyebrow">— L A N D S C A P E —</span>
        <h2 class="lp-section-title">
          삶과 쉼이 푸르게 어우러진<br/>
          <em>프리미엄 힐링 라이프</em>
        </h2>
        <div class="lp-gold-line"></div>
        <p class="land-lead">약 <strong>5,950㎡ 대형 광장</strong>의 넉넉한 개방감과 조경 특화까지,<br/>사계절의 자연을 만끽하는 일상이 시작됩니다.</p>

        <div class="land-slider">
          <div class="land-viewport">
            <div
              class="land-slide"
              v-for="(s, i) in landscapeSlides"
              :key="i"
              :class="{ active: landscapeActive === i }">
              <img :src="s.img" :alt="`김포한강푸르지오리버프론트 ${s.caption}`" loading="lazy" />
              <span class="land-caption">{{ s.caption }}</span>
            </div>
          </div>
          <div class="land-dots">
            <button
              v-for="(s, i) in landscapeSlides"
              :key="i"
              class="land-dot"
              :class="{ active: landscapeActive === i }"
              @click="goLandscape(i)"
              :aria-label="`${s.caption} 보기`"></button>
          </div>
        </div>

        <ul class="land-list">
          <li class="land-item" v-for="(f, i) in landscapeItems" :key="i">
            <h4 class="land-item-name">{{ f.name }}</h4>
            <p class="land-item-desc">{{ f.desc }}</p>
          </li>
        </ul>
      </div>
    </section>

    <!-- ===== RESERVATION CTA ===== -->
    <section class="lp-section lp-cta">
      <div class="lp-inner narrow center">
        <span class="lp-eyebrow light">— R E S E R V A T I O N —</span>
        <h2 class="lp-section-title white">
          한강을 곁에 둔 삶,<br/>
          <em>지금 예약하세요</em>
        </h2>
        <div class="lp-gold-line"></div>

        <p class="cta-desc">
          김포한강푸르지오리버프론트는 <strong>철저한 예약제</strong>로 운영됩니다.<br/>
          방문 예약 시 전문 상담을 통해 자세한 분양 정보를 안내해 드립니다.
        </p>

        <ul class="cta-points">
          <li class="cta-point">
            <span class="cta-point-icon">✓</span>
            <span class="cta-point-text">프라이빗 <strong>예약제</strong> 운영</span>
          </li>
          <li class="cta-point">
            <span class="cta-point-icon">✓</span>
            <span class="cta-point-text">1:1 <strong>전문 분양 상담</strong></span>
          </li>
          <li class="cta-point">
            <span class="cta-point-icon">✓</span>
            <span class="cta-point-text">방문 고객 <strong>우선 안내</strong></span>
          </li>
        </ul>

        <button class="cta-btn" @click="openRegister">
          <span class="cta-btn-en">PRIVATE RESERVATION</span>
          <span class="cta-btn-kr">방문 예약하기</span>
          <span class="cta-btn-arrow">→</span>
        </button>

        <p class="cta-note">
          ※ 본 사이트는 김포한강푸르지오리버프론트 분양 상담 및 방문 예약 안내 페이지입니다.<br/>
          시행사·시공사의 공식 홈페이지가 아닙니다.
        </p>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'MainView',
  data() {
    return {
      premiumItems: [
        {
          img: require('@/assets/프리미엄1.jpg'),
          tag: '한강라인',
          title: '한강라인은 더 웅장하게',
          desc: '김포의 한강라인을 새롭게 바꿀<br/>압도적 사이즈의 <strong>2,432세대 대단지 랜드마크</strong>',
        },
        {
          img: require('@/assets/프리미엄2.jpg'),
          tag: '교통 인프라',
          title: '서울 접근은 더 신속하게',
          desc: '<strong>서울 5호선(예타 통과)</strong>, 인천 2호선(계획)과<br/>김포한강로(올림픽대로) · 48번 국도로 <strong>더 빠른 서울 접근성</strong>',
        },
        {
          img: require('@/assets/프리미엄3.jpg'),
          tag: '분양가 상한제',
          title: '내 집 마련은 더 현명하게',
          desc: '<strong>분양가 상한제</strong> 적용으로 경제적 부담을 낮춘<br/>합리적 분양가 <strong>(입주 전 전매 가능)</strong>',
        },
        {
          img: require('@/assets/프리미엄4.jpg'),
          tag: '학세권',
          title: '안심 통학은 더 안전하게',
          desc: '단지 앞 유치원(계획), 초등학교(29년 예정),<br/>중학교(계획)의 <strong>도보권 통학</strong>으로 안심 교육환경',
        },
        {
          img: require('@/assets/프리미엄5.jpg'),
          tag: '미래가치',
          title: '내일의 기대는 더 크게',
          desc: '<strong>한강시네폴리스 산업단지</strong> 개발과<br/><strong>메디컬캠퍼스</strong> 계획으로 더 눈부신 미래가치',
        },
        {
          img: require('@/assets/프리미엄6.jpg'),
          tag: '커뮤니티',
          title: '힐링 라이프는 더 특별하게',
          desc: '단지 내 약 <strong>5,950㎡ 규모의 대형 광장</strong>과<br/>실내 체육관을 갖춘 특별하고 다채로운 커뮤니티',
        },
      ],

      overviewItems: [
        { label: '현장명',   value: '한강 푸르지오 리버프론트' },
        { label: '대지위치', value: '김포시 고촌읍 향산리 588-45번지 일원' },
        { label: '건축규모', value: '<span class="ov-sub"><span class="ov-tag">공동주택</span><span class="ov-txt">총 2,432세대 (지하 4층 ~ 지상 38층, 12개동)</span></span><span class="ov-sub"><span class="ov-tag">오피스텔</span><span class="ov-txt">총 250실 (지하 2층 ~ 지상 27층, 1개동)</span></span>' },
        { label: '주택형',   value: '<span class="ov-sub"><span class="ov-tag">공동주택</span><span class="ov-txt">전용 84㎡ / 106㎡ / 122㎡ / 180㎡ PH</span></span><span class="ov-sub"><span class="ov-tag">오피스텔</span><span class="ov-txt">전용 84㎡ / 90㎡</span></span>' },
      ],

      locationItems: [
        { no: '01', en: 'Speed', title: '더 빨라지는 서울', desc: '5호선(예타 통과), 인천2호선(계획),<br/>김포한강로(올림픽대로) · 48번 국도가 잇는 <strong>쾌속 교통망</strong>' },
        { no: '02', en: 'Access', title: '더 가까워지는 한강', desc: '탁 트인 한강 라인과 <strong>영구조망(일부 세대)</strong>으로<br/>삶의 품격을 높이는 한강 라이프' },
        { no: '03', en: 'Education', title: '더 든든해지는 교육', desc: '단지 앞 유치원(계획), 초등학교(29년 예정),<br/>중학교(계획)로 <strong>원스톱 학세권</strong>' },
        { no: '04', en: 'Lifestyle', title: '더 풍부해지는 생활', desc: '산업단지 직주근접과 <strong>이마트 트레이더스</strong>,<br/><strong>현대프리미엄아울렛</strong> 등 풍족한 인프라' },
      ],

      communityZones: [
        {
          floor: 'B1F / B2F',
          name: 'SPORTS ZONE',
          tagline: '건강과 균형이 공존하는 웰니스 공간',
          img: require('@/assets/community_1.v100.png'),
          facilities: [
            { name: '실내체육관', desc: '계절과 날씨의 영향 없이 다채로운 생활체육을 즐길 수 있는 공간' },
            { name: '골프 클럽', desc: '단지 안에서도 필드의 즐거움을 누리는 프리미엄 골프 연습장' },
            { name: 'GX룸', desc: '요가, 에어로빅 등으로 몸과 마음의 균형을 다지는 운동 공간' },
            { name: '사우나', desc: '운동 후에도 평소에도 피로를 회복하고 스트레스를 푸는 힐링 공간' },
            { name: '피트니스 클럽', desc: '다양한 운동기구로 활기와 활력을 채우는 체력 증진 공간' },
            { name: '탁구 & 당구장', desc: '가벼운 활력을 더하고 여유로운 플레이를 즐기는 입주민 소통 공간' },
          ],
        },
        {
          floor: '1F',
          name: 'CULTURE & EDU ZONE',
          tagline: '세상 어디에도 없던 소중한 시공간, 꿈꾸던 라이프 컬처의 완성',
          img: require('@/assets/community_2.v100.png'),
          facilities: [
            { name: '그리너리 스튜디오', desc: '교육, 문화, 업무가 조화롭게 어우러지는 프리미엄 복합 문화 공간' },
            { name: '그리너리 카페', desc: '차 한잔의 여유와 함께 자신의 라이프 스타일에 집중할 수 있는 공간' },
            { name: '시네마룸', desc: '대형 스크린으로 영화와 다양한 콘텐츠를 함께 즐길 수 있는 공간' },
            { name: '공유오피스', desc: '입주민들을 위해 마련된 1인실의 프리미엄 학습 공간 및 업무 공간' },
            { name: '게스트 하우스', desc: '귀한 손님을 위한 숙소, 가족의 모임·파티 장소 등으로 활용할 수 있는 공간' },
            { name: '시니어 클럽', desc: '어르신들이 즐겁고 편안하게 담소와 취미를 나눌 수 있는 여가 공간' },
            { name: '다함께 돌봄센터', desc: '초등학교 자녀가 안전하게 머무를 수 있어 안심되는 돌봄 공간' },
          ],
        },
      ],
      communityNotes: [
        '상기 자료는 소비자의 이해를 돕기 위한 것으로 실제와 차이가 있을 수 있으며, 인허가 과정이나 구조 및 성능 개선을 위하여 계획이 일부 변경될 수 있습니다.',
        'Greenery Lounge(그리너리 라운지)는 푸르지오의 주민편의시설을 의미합니다.',
        '본 공사 시 마감재 및 레이아웃은 현장상황에 따라 변경될 수 있습니다.',
        'Greenery Lounge 중 일부 공간은 일정기간 푸르지오 A/S센터로 사용될 수 있습니다.',
        'Greenery Lounge 내 가구 수량 및 배치는 실제 시공 시 이미지와 차이가 날 수 있으며 기타 집기류는 제공되지 않습니다.',
        '커뮤니티(Greenery Lounge) 시설의 운영 및 관리 등은 입주 후 입주자 부담으로 입주자가 직접 운영하여야 합니다.',
      ],

      // 단지 조경
      landscapeSlides: [
        { img: require('@/assets/중앙광장.jpg'), caption: '중앙광장' },
        { img: require('@/assets/바닥분수.jpg'), caption: '바닥분수 광장' },
        { img: require('@/assets/놀이숲.jpg'), caption: '놀이숲' },
      ],
      landscapeActive: 0,
      landscapeTimer: null,
      landscapeItems: [
        { name: '중앙광장', desc: '놀이와 휴게공간이 어우러진 약 5,950㎡의 커뮤니티 광장' },
        { name: '주민운동시설', desc: '입주민 한 사람 한 사람의 평생 건강을 위한 체력증진 공간' },
        { name: '바닥분수 광장', desc: '쾌적하고 시원한 물줄기가 일상의 스트레스를 씻어주는 쉼터' },
        { name: '놀이숲', desc: '웃음소리가 끊이지 않는 놀이터와 이어진 숲 속 놀이 공간' },
        { name: '휴게정원', desc: '게스트하우스 앞 싱그러운 나무와 꽃이 우거진 휴게 정원' },
      ],

      unitTypes: [
        { label: '84㎡A', image: '84A.jpg', count: '888', ex: '84.7402', sup: '113.0210', resi: '28.2808', etc: '56.3034', con: '169.3243' },
        { label: '84㎡B', image: '84B.jpg', count: '68', ex: '84.5605', sup: '113.2751', resi: '28.7146', etc: '56.1840', con: '169.4591' },
        { label: '84㎡C', image: '84C.jpg', count: '537', ex: '84.9219', sup: '112.5506', resi: '27.6287', etc: '56.4241', con: '168.9747' },
        { label: '84㎡D', image: '84D.jpg', count: '148', ex: '84.8168', sup: '113.9384', resi: '29.1216', etc: '56.3543', con: '170.2927' },
        { label: '84㎡E', image: '84E.jpg', count: '98', ex: '84.7060', sup: '112.9669', resi: '28.2609', etc: '56.2807', con: '169.2475' },
        { label: '84㎡F', image: '84F.jpg', count: '36', ex: '84.9001', sup: '112.6296', resi: '27.7295', etc: '56.4096', con: '169.0392' },
        { label: '106㎡A', image: '106A.jpg', count: '288', ex: '106.8713', sup: '141.6004', resi: '34.7291', etc: '71.0078', con: '212.6082' },
        { label: '106㎡B', image: '106B.jpg', count: '144', ex: '106.9778', sup: '141.5485', resi: '34.5707', etc: '71.0786', con: '212.6271' },
        { label: '122㎡', image: '112.jpg', count: '216', ex: '122.7490', sup: '163.0800', resi: '40.3310', etc: '81.5573', con: '244.6373' },
        { label: '180㎡A', image: '180A.jpg', count: '4', ex: '180.2607', sup: '237.3821', resi: '57.1214', etc: '119.7695', con: '357.1516' },
        { label: '180㎡B', image: '180B.jpg', count: '2', ex: '180.9387', sup: '238.7052', resi: '57.7665', etc: '120.2199', con: '358.9251' },
        { label: '180㎡C', image: '180C.jpg', count: '3', ex: '180.9315', sup: '237.6252', resi: '56.6937', etc: '120.2151', con: '357.8403' },
      ],
      activeUnit: 0,
      unitLoading: true,

      // 사업개요 조감도 롤링
      overviewSlides: [
        require('@/assets/조감도1.jpg'),
        require('@/assets/조감도2.jpg'),
        require('@/assets/조감도3.jpg'),
      ],
      activeSlide: 0,
      slideTimer: null,

      // 프리미엄 카드 스크롤 리빌
      premiumIn: [],
      premiumObserver: null,
    }
  },
  computed: {
    currentUnit() {
      return this.unitTypes[this.activeUnit]
    }
  },
  mounted() {
    this.preloadUnitImages()
    this.startSlideShow()
    this.startLandscapeShow()
    this.setupPremiumReveal()
  },
  beforeUnmount() {
    this.stopSlideShow()
    this.stopLandscapeShow()
    if (this.premiumObserver) this.premiumObserver.disconnect()
  },
  methods: {
    openRegister() {
      window.dispatchEvent(new Event('open-register-popup'))
    },
    startSlideShow() {
      this.stopSlideShow()
      this.slideTimer = setInterval(() => {
        this.activeSlide = (this.activeSlide + 1) % this.overviewSlides.length
      }, 3800)
    },
    stopSlideShow() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer)
        this.slideTimer = null
      }
    },
    goSlide(i) {
      this.activeSlide = i
      this.startSlideShow()
    },
    startLandscapeShow() {
      this.stopLandscapeShow()
      this.landscapeTimer = setInterval(() => {
        this.landscapeActive = (this.landscapeActive + 1) % this.landscapeSlides.length
      }, 4200)
    },
    stopLandscapeShow() {
      if (this.landscapeTimer) {
        clearInterval(this.landscapeTimer)
        this.landscapeTimer = null
      }
    },
    goLandscape(i) {
      this.landscapeActive = i
      this.startLandscapeShow()
    },
    setupPremiumReveal() {
      const cards = this.$refs.premCards
      if (!cards || !cards.length) return
      // IntersectionObserver 미지원 환경은 즉시 표시
      if (typeof IntersectionObserver === 'undefined') {
        this.premiumIn = cards.map((_, i) => i)
        return
      }
      this.premiumObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = cards.indexOf(entry.target)
            if (idx !== -1 && !this.premiumIn.includes(idx)) {
              this.premiumIn.push(idx)
            }
            this.premiumObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' })
      cards.forEach((el) => this.premiumObserver.observe(el))
    },
    getUnitImg(name) {
      return require('@/assets/unit/' + name)
    },
    setUnit(idx) {
      if (this.activeUnit === idx) return
      this.unitLoading = true
      this.activeUnit = idx
    },
    onUnitImgLoad() {
      this.unitLoading = false
    },
    preloadUnitImages() {
      this.unitTypes.forEach(u => {
        const img = new Image()
        img.src = require('@/assets/unit/' + u.image)
      })
    }
  }
}
</script>

<style scoped>
/* ==================== ROOT ==================== */
.lp {
  background: #ffffff;
  color: var(--le-ink);
  font-family: var(--font-sans);
  letter-spacing: -0.01em;
  overflow-x: hidden;
}

.lp em {
  font-family: var(--font-serif-en);
  font-style: italic;
  font-weight: 400;
  color: var(--le-champagne-dark);
}

.lp strong { font-weight: 600; color: var(--le-noir); }

/* 어절 단위 줄바꿈 — 어느 화면 폭에서든 단어가 중간에 잘리지 않게 */
.lp h2,
.lp h3,
.lp p,
.lp dd {
  word-break: keep-all;
  overflow-wrap: break-word;
}

.lp-inner {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 22px;
}
.lp-inner.narrow { max-width: 760px; }
.lp-inner.center { text-align: center; }

.lp-section {
  padding: 96px 0;
  position: relative;
}

.lp-eyebrow {
  display: block;
  font-family: var(--font-mono-disp);
  font-size: 11px;
  letter-spacing: 0.38em;
  font-weight: 500;
  color: var(--le-champagne-dark);
  margin-bottom: 22px;
  text-align: center;
}
.lp-eyebrow.light { color: var(--le-champagne); }

.lp-section-title {
  font-family: var(--font-serif-kr);
  font-weight: 300;
  font-size: 32px;
  line-height: 1.45;
  color: var(--le-noir);
  letter-spacing: -0.02em;
  margin-bottom: 28px;
  text-align: center;
}
.lp-section-title em {
  font-family: var(--font-serif-en);
  font-weight: 400;
  font-style: italic;
  color: var(--le-champagne-dark);
}
.lp-section-title.white { color: var(--le-ink); }
.lp-section-title.white em { color: var(--le-champagne-dark); }
.lp-section-title.light { font-weight: 200; }

.lp-gold-line {
  width: 60px; height: 1px;
  background: linear-gradient(90deg, rgba(0, 48, 48,0), var(--le-champagne), rgba(0, 48, 48,0));
  margin: 0 auto 40px;
}
.lp-gold-line.short { width: 36px; margin: 18px 0; }

/* ==================== HERO IMAGE ==================== */
.lp-hero-img {
  width: 100%;
  background: #ffffff;
  line-height: 0;
}
.lp-hero-img img {
  display: block;
  width: 100%;
  height: auto;
}

/* ==================== OVERVIEW SLIDER (조감도 롤링) ==================== */
.lp-ovslider {
  width: 100%;
  background: #ffffff;
  padding: 0 0 20px;
}
.ovs-viewport {
  position: relative;
  width: 100%;
  aspect-ratio: 899 / 525;
  overflow: hidden;
  background: var(--le-noir);
}
.ovs-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 1s ease;
  pointer-events: none;
}
.ovs-slide.active {
  opacity: 1;
  pointer-events: auto;
}
.ovs-slide img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
.ovs-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px 0 4px;
}
.ovs-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(0, 48, 48, 0.22);
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.ovs-dot.active {
  width: 24px;
  background: var(--le-champagne);
}

/* ==================== THE PREMIUM (6) ==================== */
.lp-premium {
  background: linear-gradient(180deg, #ffffff 0%, var(--le-cream) 100%);
  padding: 110px 0 120px;
  position: relative;
}
.lp-premium::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, var(--le-champagne), transparent);
  opacity: 0.5;
}

.prem-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-top: 20px;
}

.prem-card {
  background: #ffffff;
  border: 1px solid rgba(0, 48, 48, 0.12);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 40px -28px rgba(0, 48, 48, 0.35);
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.7s cubic-bezier(0.16, 1, 0.3, 1),
              box-shadow 0.35s ease, border-color 0.35s ease;
  will-change: opacity, transform;
}
.prem-card.is-in {
  opacity: 1;
  transform: translateY(0);
}
.prem-card:hover {
  border-color: rgba(0, 48, 48, 0.28);
  box-shadow: 0 26px 50px -26px rgba(0, 48, 48, 0.45);
}

/* 이미지 */
.prem-fig {
  position: relative;
  margin: 0;
  aspect-ratio: 11 / 6;
  overflow: hidden;
  background: var(--le-pearl);
}
.prem-fig img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1.02);
  transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
}
.prem-card:hover .prem-fig img {
  transform: scale(1.09);
}
.prem-fig::after {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0, 26, 24, 0) 55%, rgba(0, 26, 24, 0.32) 100%);
  pointer-events: none;
}
.prem-chip {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  font-family: var(--font-rounded);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--le-champagne-dark);
  box-shadow: 0 4px 12px -6px rgba(0, 48, 48, 0.4);
}

/* 본문 */
.prem-body {
  padding: 16px 15px 20px;
}
.prem-title {
  font-family: var(--font-rounded);
  font-size: 15.5px;
  font-weight: 700;
  color: var(--le-ink);
  letter-spacing: -0.02em;
  line-height: 1.35;
  margin: 0 0 8px;
  word-break: keep-all;
}
.prem-desc {
  font-family: var(--font-sans);
  font-weight: 400;
  font-size: 12.5px;
  line-height: 1.65;
  color: rgba(0, 26, 24, 0.68);
  letter-spacing: -0.01em;
  word-break: keep-all;
  overflow-wrap: break-word;
  margin: 0;
}
.prem-desc :deep(strong) {
  color: var(--le-champagne-dark);
  font-weight: 700;
}

@media (prefers-reduced-motion: reduce) {
  .prem-card { opacity: 1; transform: none; transition: none; }
  .prem-fig img,
  .prem-card:hover .prem-fig img { transform: none; transition: none; }
}

/* ==================== OVERVIEW (사업 개요) ==================== */
.lp-overview-head {
  background: #ffffff;
  padding: 80px 0 36px;
}
.lp-overview {
  background: #ffffff;
  padding: 36px 0 90px;
}
.ov-list {
  margin: 8px 0 0;
  padding: 0;
  border-top: 2px solid var(--le-champagne);
  border-bottom: 1px solid rgba(11, 11, 15, 0.08);
}
.ov-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 16px;
  padding: 18px 4px;
  border-bottom: 1px solid rgba(11, 11, 15, 0.06);
  align-items: start;
}
.ov-row:last-child {
  border-bottom: none;
}
.ov-label {
  font-family: var(--font-rounded);
  font-size: 13px;
  font-weight: 700;
  color: var(--le-champagne-dark);
  letter-spacing: -0.01em;
  line-height: 1.6;
}
.ov-value {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  color: var(--le-ink);
  line-height: 1.6;
  letter-spacing: -0.01em;
  word-break: keep-all;
  margin: 0;
}
.ov-value :deep(.ov-sub) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.ov-value :deep(.ov-sub + .ov-sub) {
  margin-top: 8px;
}
.ov-value :deep(.ov-tag) {
  flex: 0 0 auto;
  min-width: 58px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--le-pearl);
  color: var(--le-champagne-dark);
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.01em;
  line-height: 1.5;
}
.ov-value :deep(.ov-txt) {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 1px;
}
.ov-note {
  margin: 16px 0 0;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  color: var(--le-warm-grey);
  letter-spacing: -0.01em;
}

/* ==================== LOCATION (입지환경) ==================== */
.lp-location {
  background: #ffffff;
  padding: 90px 0 110px;
}
.loc-lead {
  text-align: center;
  font-family: var(--font-serif-kr);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.7;
  color: var(--le-ink);
  margin: 0 0 34px;
  word-break: keep-all;
}
.loc-lead strong {
  color: var(--le-champagne-dark);
  font-weight: 500;
}
.loc-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
.loc-item {
  display: flex;
  flex-direction: column;
  padding: 20px 16px 22px;
  background: #ffffff;
  border: 1px solid rgba(0, 48, 48, 0.14);
  border-radius: 16px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
.loc-item:hover {
  border-color: rgba(0, 48, 48, 0.3);
  box-shadow: 0 16px 34px -22px rgba(0, 48, 48, 0.4);
  transform: translateY(-2px);
}
.loc-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.loc-num {
  font-family: var(--font-rounded);
  font-size: 12px;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(135deg, var(--le-champagne), var(--le-champagne-dark));
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.02em;
  line-height: 1.5;
}
.loc-en {
  font-family: var(--font-mono-disp);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--le-champagne);
}
.loc-title {
  font-family: var(--font-rounded);
  font-size: 16.5px;
  font-weight: 700;
  color: var(--le-ink);
  margin: 0 0 8px;
  letter-spacing: -0.02em;
  line-height: 1.3;
  word-break: keep-all;
}
.loc-desc {
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.65;
  color: rgba(0, 26, 24, 0.68);
  letter-spacing: -0.01em;
  word-break: keep-all;
  overflow-wrap: break-word;
  margin: 0;
}
.loc-desc :deep(strong) {
  color: var(--le-champagne-dark);
  font-weight: 700;
}
.loc-map {
  margin: 34px 0 0;
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0, 48, 48, 0.18);
}
.loc-map img {
  display: block;
  width: 100%;
  height: auto;
}

/* ==================== PLOT PLAN ==================== */
.lp-plot {
  background: #ffffff;
  padding: 110px 0 120px;
}
.lp-plot .lp-gold-line {
  margin-left: auto;
  margin-right: auto;
}
.plot-lead {
  font-family: var(--font-serif-kr);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.9;
  color: var(--le-ink);
  text-align: center;
  margin-top: 4px;
  margin-bottom: 36px;
}
.plot-lead strong {
  color: var(--le-champagne-dark, #15584C);
  font-weight: 500;
}
.plot-item {
  margin-top: 30px;
}
.plot-item:first-of-type {
  margin-top: 8px;
}
.plot-subtitle {
  font-family: var(--font-rounded);
  font-size: 15px;
  font-weight: 700;
  color: var(--le-champagne-dark);
  letter-spacing: 0.02em;
  margin-bottom: 14px;
}
.plot-fig {
  margin: 0;
  position: relative;
  padding: 14px;
  background: linear-gradient(180deg, #E7F0ED 0%, #DCE3F2 100%);
  border: 1px solid rgba(0, 48, 48, 0.35);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.4) inset,
    0 24px 50px -20px rgba(11, 11, 15, 0.18);
}
.plot-fig::before,
.plot-fig::after {
  content: '';
  position: absolute;
  width: 14px; height: 14px;
  border: 1px solid var(--le-champagne, #15584C);
  opacity: 0.7;
}
.plot-fig::before { top: 4px; left: 4px; border-right: none; border-bottom: none; }
.plot-fig::after  { bottom: 4px; right: 4px; border-left: none; border-top: none; }
.plot-img {
  display: block;
  width: 100%;
  height: auto;
}
.plot-cap {
  margin-top: 14px;
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--le-warm-grey, #6C8C84);
  text-align: center;
  letter-spacing: 0.02em;
}

/* ==================== COMMUNITY ==================== */
.lp-community {
  background: linear-gradient(180deg, var(--le-cream) 0%, #ffffff 100%);
  padding: 110px 0 120px;
}
.comm-lead {
  text-align: center;
  font-family: var(--font-serif-kr);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.7;
  color: var(--le-ink);
  margin: 0 0 8px;
  word-break: keep-all;
}
.comm-lead strong {
  color: var(--le-champagne-dark);
  font-weight: 500;
}

/* 존 */
.comm-zone {
  margin-top: 54px;
}
.comm-zone-head {
  text-align: center;
  margin-bottom: 20px;
}
.comm-zone-floor {
  display: inline-block;
  font-family: var(--font-mono-disp);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #ffffff;
  background: linear-gradient(135deg, var(--le-champagne), var(--le-champagne-dark));
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: 12px;
}
.comm-zone-name {
  font-family: var(--font-mono-disp);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: var(--le-ink);
  margin: 0 0 8px;
}
.comm-zone-tagline {
  font-family: var(--font-serif-kr);
  font-size: 13.5px;
  font-weight: 300;
  color: rgba(0, 26, 24, 0.7);
  letter-spacing: -0.01em;
  word-break: keep-all;
  margin: 0;
}

/* 배치도 */
.comm-map {
  margin: 0 0 22px;
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0, 48, 48, 0.16);
  background: var(--le-pearl);
}
.comm-map img {
  display: block;
  width: 100%;
  height: auto;
}

/* 시설 리스트 */
.comm-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.comm-item {
  padding: 16px 15px 18px;
  background: #ffffff;
  border: 1px solid rgba(0, 48, 48, 0.12);
  border-radius: 14px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
.comm-item:hover {
  border-color: rgba(0, 48, 48, 0.28);
  box-shadow: 0 14px 30px -20px rgba(0, 48, 48, 0.4);
  transform: translateY(-2px);
}
.comm-item-name {
  font-family: var(--font-rounded);
  font-size: 15px;
  font-weight: 700;
  color: var(--le-champagne-dark);
  letter-spacing: -0.02em;
  margin: 0 0 7px;
  padding-left: 12px;
  position: relative;
  word-break: keep-all;
}
.comm-item-name::before {
  content: '';
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 3px;
  width: 3px;
  border-radius: 3px;
  background: linear-gradient(180deg, var(--le-champagne), var(--le-champagne-dark));
}
.comm-item-desc {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(0, 26, 24, 0.66);
  letter-spacing: -0.01em;
  word-break: keep-all;
  overflow-wrap: break-word;
  margin: 0;
}

/* 유의사항 */
.comm-notes {
  margin-top: 40px;
  padding-top: 22px;
  border-top: 1px solid rgba(0, 48, 48, 0.12);
}
.comm-notes p {
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 400;
  line-height: 1.6;
  color: var(--le-warm-grey);
  letter-spacing: -0.01em;
  margin: 0 0 5px;
  word-break: keep-all;
}

/* ==================== UNIT ==================== */
.lp-unit {
  background: var(--le-pearl);
  padding: 110px 0;
}
.lp-unit .lp-gold-line {
  margin-left: auto;
  margin-right: auto;
}
.unit-tabs {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  margin: 8px 0 28px;
  padding: 4px 0;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.unit-tabs::-webkit-scrollbar { display: none; }

.unit-tab {
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid rgba(0, 48, 48, 0.3);
  padding: 10px 18px;
  cursor: pointer;
  font-family: var(--font-rounded);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--le-ink);
  border-radius: 999px;
  transition: all 0.25s ease;
}
.unit-tab:hover {
  border-color: var(--le-champagne);
}
.unit-tab.active {
  background: var(--le-champagne);
  border-color: var(--le-champagne);
  color: #ffffff;
  box-shadow: 0 8px 20px -10px rgba(0, 48, 48, 0.5);
}

.unit-panel {
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid rgba(0, 48, 48, 0.14);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 40px -28px rgba(0, 48, 48, 0.35);
  text-align: left;
}
.unit-img-wrap {
  position: relative;
  width: 100%;
  min-height: 240px;
  background: #ffffff;
  overflow: hidden;
}
.unit-img {
  display: block;
  width: 100%;
  height: auto;
  transition: opacity 0.35s ease;
}
.unit-img.is-loading {
  opacity: 0.3;
}

.unit-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  pointer-events: none;
}
.unit-spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(0, 48, 48, 0.2);
  border-top-color: var(--le-champagne, #15584C);
  animation: unitSpin 0.8s linear infinite;
}
.unit-loading-label {
  font-family: var(--font-sans);
  font-size: 10px;
  letter-spacing: 0.42em;
  color: var(--le-champagne-dark, #003030);
  font-weight: 600;
}

@keyframes unitSpin {
  to { transform: rotate(360deg); }
}

.unit-fade-enter-active,
.unit-fade-leave-active {
  transition: opacity 0.3s ease;
}
.unit-fade-enter-from,
.unit-fade-leave-to {
  opacity: 0;
}

/* 유닛 정보 패널 */
.unit-info {
  padding: 22px 20px 24px;
  border-top: 1px solid rgba(0, 48, 48, 0.1);
}
.unit-info-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(0, 48, 48, 0.1);
}
.unit-info-type {
  font-family: var(--font-rounded);
  font-size: 22px;
  font-weight: 800;
  color: var(--le-ink);
  letter-spacing: -0.02em;
}
.unit-info-count {
  flex-shrink: 0;
  font-family: var(--font-rounded);
  font-size: 12.5px;
  font-weight: 700;
  color: var(--le-champagne-dark);
  background: var(--le-pearl);
  padding: 5px 12px;
  border-radius: 999px;
  letter-spacing: -0.01em;
}
.unit-spec {
  margin: 0;
}
.unit-spec-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 2px;
  border-bottom: 1px solid rgba(0, 48, 48, 0.07);
}
.unit-spec-row dt {
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--le-warm-grey);
  letter-spacing: -0.01em;
}
.unit-spec-row dd {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 13.5px;
  font-weight: 600;
  color: var(--le-ink);
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}
.unit-spec-total {
  border-bottom: none;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1.5px solid rgba(0, 48, 48, 0.16);
}
.unit-spec-total dt {
  color: var(--le-champagne-dark);
  font-weight: 700;
}
.unit-spec-total dd {
  color: var(--le-champagne-dark);
  font-weight: 800;
  font-size: 15.5px;
}
.unit-note {
  margin: 16px 0 0;
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--le-warm-grey);
  text-align: center;
  letter-spacing: -0.01em;
}

/* ==================== LANDSCAPE (단지 조경) ==================== */
.lp-landscape {
  background: linear-gradient(180deg, #ffffff 0%, var(--le-cream) 100%);
  padding: 110px 0 120px;
}
.land-lead {
  font-family: var(--font-serif-kr);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.75;
  color: var(--le-ink);
  margin: 0 0 30px;
  word-break: keep-all;
}
.land-lead strong {
  color: var(--le-champagne-dark);
  font-weight: 500;
}

.land-slider {
  margin: 0 0 26px;
}
.land-viewport {
  position: relative;
  width: 100%;
  aspect-ratio: 599 / 350;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(0, 48, 48, 0.14);
  background: var(--le-noir);
  box-shadow: 0 18px 40px -28px rgba(0, 48, 48, 0.4);
}
.land-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 1s ease;
  pointer-events: none;
}
.land-slide.active {
  opacity: 1;
  pointer-events: auto;
}
.land-slide img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
.land-caption {
  position: absolute;
  left: 14px;
  bottom: 14px;
  z-index: 2;
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(0, 26, 24, 0.55);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  font-family: var(--font-rounded);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff;
}
.land-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px 0 0;
}
.land-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(0, 48, 48, 0.22);
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.land-dot.active {
  width: 24px;
  background: var(--le-champagne);
}

.land-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  text-align: left;
}
.land-item {
  padding: 16px 15px 18px;
  background: #ffffff;
  border: 1px solid rgba(0, 48, 48, 0.12);
  border-radius: 14px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
.land-item:hover {
  border-color: rgba(0, 48, 48, 0.28);
  box-shadow: 0 14px 30px -20px rgba(0, 48, 48, 0.4);
  transform: translateY(-2px);
}
.land-item-name {
  font-family: var(--font-rounded);
  font-size: 15px;
  font-weight: 700;
  color: var(--le-champagne-dark);
  letter-spacing: -0.02em;
  margin: 0 0 7px;
  padding-left: 12px;
  position: relative;
  word-break: keep-all;
}
.land-item-name::before {
  content: '';
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 3px;
  width: 3px;
  border-radius: 3px;
  background: linear-gradient(180deg, var(--le-champagne), var(--le-champagne-dark));
}
.land-item-desc {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(0, 26, 24, 0.66);
  letter-spacing: -0.01em;
  word-break: keep-all;
  overflow-wrap: break-word;
  margin: 0;
}

/* ==================== CTA ==================== */
.lp-cta {
  background: #ffffff;
  color: var(--le-ink);
  padding: 110px 0 130px;
  position: relative;
  overflow: hidden;
}
.lp-cta::before {
  content: '';
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse at center, rgba(0, 48, 48,0.08), transparent 60%);
  pointer-events: none;
}
.lp-cta .lp-inner { position: relative; z-index: 2; }
.lp-cta .lp-gold-line { margin-left: auto; margin-right: auto; }
.cta-desc {
  font-family: var(--font-serif-kr);
  font-weight: 400;
  font-size: 16px;
  line-height: 2;
  color: rgba(11,11,15,0.78);
  margin-bottom: 26px;
}
.cta-desc strong {
  color: var(--le-champagne-dark);
  font-weight: 600;
}
.cta-points {
  list-style: none;
  margin: 0 auto 34px;
  padding: 0;
  display: inline-flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}
.cta-point {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cta-point-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--le-champagne), var(--le-champagne-dark));
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}
.cta-point-text {
  font-family: var(--font-sans);
  font-size: 14.5px;
  font-weight: 500;
  color: var(--le-ink);
  letter-spacing: -0.01em;
}
.cta-point-text strong {
  color: var(--le-champagne-dark);
  font-weight: 700;
}
.cta-btn {
  background: var(--le-champagne);
  border: 1px solid var(--le-champagne);
  color: #ffffff;
  padding: 22px 30px;
  font-family: inherit;
  cursor: pointer;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
}
.cta-btn::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(135deg, var(--le-champagne-dark), var(--le-champagne));
  transform: translateY(101%);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 0;
}
.cta-btn:hover::before { transform: translateY(0); }
.cta-btn > * { position: relative; z-index: 1; }
.cta-btn:hover { color: #ffffff; border-color: var(--le-champagne-dark); }
.cta-btn-en {
  font-family: var(--font-mono-disp);
  font-size: 11px;
  letter-spacing: 0.4em;
  color: #ffffff;
  font-weight: 500;
  transition: color 0.4s;
}
.cta-btn-kr {
  font-family: var(--font-serif-kr);
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.cta-btn-arrow {
  font-family: var(--font-serif-en);
  font-size: 18px;
  margin-top: 4px;
  color: #ffffff;
  transition: all 0.4s;
}
.cta-btn:hover .cta-btn-arrow { transform: translateX(4px); }

.cta-note {
  margin-top: 36px;
  font-family: var(--font-serif-kr);
  font-size: 11px;
  color: rgba(11,11,15,0.45);
  line-height: 1.8;
  font-weight: 400;
}

/* ==================== RESPONSIVE (Tablet+) ==================== */
@media (min-width: 768px) {
  .lp-inner { padding: 0 40px; }
  .lp-section { padding: 130px 0; }

  .lp-section-title { font-size: 44px; margin-bottom: 36px; }
  .lp-eyebrow { font-size: 12px; margin-bottom: 28px; }
  .lp-gold-line { width: 80px; margin-bottom: 56px; }


  .prem-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }
  .prem-body { padding: 26px 24px 30px; }
  .prem-title { font-size: 21px; }
  .prem-desc { font-size: 14px; }

  .loc-lead { font-size: 17px; margin-bottom: 44px; }
  .loc-list { gap: 22px; }
  .loc-item { padding: 30px 28px 32px; }
  .loc-title { font-size: 20px; }
  .loc-desc { font-size: 14px; line-height: 1.75; }
  .loc-num { font-size: 13px; }
  .loc-en { font-size: 12px; }

  .comm-zone { margin-top: 72px; }
  .comm-zone-name { font-size: 26px; }
  .comm-list { grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .comm-item { padding: 20px 18px 22px; }
  .comm-item-name { font-size: 16px; }

  .unit-info { padding: 28px 30px 30px; }
  .unit-info-type { font-size: 26px; }
  .unit-note { font-size: 12px; }

  .land-lead { font-size: 17px; margin-bottom: 40px; }
  .land-list { grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .land-item { padding: 20px 18px 22px; }
  .land-item-name { font-size: 16px; }
  .land-caption { font-size: 13px; }
}

@media (min-width: 1024px) {
  .lp-section { padding: 160px 0; }
  .lp-section-title { font-size: 52px; }
  .prem-grid { gap: 32px; }
  .loc-list { gap: 26px; }

  .comm-list { gap: 16px; }
  .land-list { gap: 16px; }

  .unit-panel {
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    align-items: stretch;
  }
  .unit-img-wrap { border-right: 1px solid rgba(0, 48, 48, 0.1); }
  .unit-info {
    border-top: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
